/**
 * Enhanced Contract Clause Mapping System
 * Maps contract data to negotiable clauses with proper validation and hierarchy
 */

import {
  CONTRACT_APPRAISAL_CONTINGENCY,
  CONTRACT_FINANCING_CONTINGENCY,
  CONTRACT_FINANCING_CONTINGENCY_TYPES,
  CONTRACT_HOME_INSPECTION_CONTINGENCY,
  CONTRACT_INSPECTION_CONTINGENCY,
  CONTRACT_POST_CLOSE_OCCUPANCY,
} from '@/config/constants';
import { Contract } from '@/types/ContractType';
import {
  ClauseProperties,
  generateClauseIdWithProperties,
} from './clauseIdUtils';

export interface ClauseData {
  id: string;
  hierarchicalId?: string;
  title: string;
  description: string;
  value: any;
  originalValue: any;
  category:
    | 'price'
    | 'timeline'
    | 'contingency'
    | 'inspection'
    | 'financing'
    | 'occupancy'
    | 'other';
  isNegotiable: boolean;
  isRequired: boolean;
  negotiationStatus?: 'original' | 'countered' | 'accepted' | 'rejected';
  proposedBy?: 'buyer' | 'seller' | 'agent';
  parentClauseId?: string;
  subClauses?: ClauseData[];
  properties?: ClauseProperties;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    required?: boolean;
  };
}

export interface ClauseCategory {
  id: string;
  title: string;
  description: string;
  clauses: ClauseData[];
  isCollapsible: boolean;
  isExpanded: boolean;
}

/**
 * Extract all negotiable clauses from contract data
 */
export function extractContractClauses(contract: Contract): ClauseCategory[] {
  if (!contract) return [];

  const categories: ClauseCategory[] = [];

  // Price Category
  const priceClauses = extractPriceClauses(contract);
  if (priceClauses.length > 0) {
    categories.push({
      id: 'price',
      title: 'Price & Financial Terms',
      description: 'Purchase price, deposits, and financial contingencies',
      clauses: priceClauses,
      isCollapsible: true,
      isExpanded: true,
    });
  }

  // Timeline Category
  const timelineClauses = extractTimelineClauses(contract);
  if (timelineClauses.length > 0) {
    categories.push({
      id: 'timeline',
      title: 'Timeline & Deadlines',
      description: 'Closing dates, inspection periods, and other deadlines',
      clauses: timelineClauses,
      isCollapsible: true,
      isExpanded: true,
    });
  }

  // Contingency Category
  const contingencyClauses = extractContingencyClauses(contract);
  if (contingencyClauses.length > 0) {
    categories.push({
      id: 'contingency',
      title: 'Contingencies',
      description: 'Appraisal, financing, and inspection contingencies',
      clauses: contingencyClauses,
      isCollapsible: true,
      isExpanded: true,
    });
  }

  // Inspection Category
  const inspectionClauses = extractInspectionClauses(contract);
  if (inspectionClauses.length > 0) {
    categories.push({
      id: 'inspection',
      title: 'Inspections',
      description: 'Home inspection terms and conditions',
      clauses: inspectionClauses,
      isCollapsible: true,
      isExpanded: true,
    });
  }

  // Occupancy Category
  const occupancyClauses = extractOccupancyClauses(contract);
  if (occupancyClauses.length > 0) {
    categories.push({
      id: 'occupancy',
      title: 'Occupancy & Possession',
      description: 'Post-closing occupancy and possession terms',
      clauses: occupancyClauses,
      isCollapsible: true,
      isExpanded: true,
    });
  }

  return categories;
}

/**
 * Extract price-related clauses
 */
function extractPriceClauses(contract: Contract): ClauseData[] {
  const clauses: ClauseData[] = [];

  // Offer Price
  if (contract.agentOfferInfo?.offerPrice) {
    clauses.push({
      id: 'offer-price',
      title: 'Purchase Price',
      description: 'The total purchase price for this property',
      value: contract.agentOfferInfo.offerPrice,
      originalValue: contract.agentOfferInfo.offerPrice,
      category: 'price',
      isNegotiable: true,
      isRequired: true,
      validation: {
        min: 1,
        required: true,
      },
    });
  }

  // Earnest Money Deposit
  if (contract.agentOfferInfo?.deposit1Days) {
    clauses.push({
      id: 'earnest-money',
      title: 'Earnest Money Deposit',
      description: `Initial earnest money deposit of $${contract.agentOfferInfo.deposit1Days}`,
      value: contract.agentOfferInfo.deposit1Days,
      originalValue: contract.agentOfferInfo.deposit1Days,
      category: 'price',
      isNegotiable: true,
      isRequired: false,
      validation: {
        min: 0,
        required: false,
      },
    });
  }

  // Down Payment
  if (contract.offerSpecifics?.downPayment) {
    clauses.push({
      id: 'down-payment',
      title: 'Down Payment',
      description: `Down payment amount: $${contract.offerSpecifics.downPayment}`,
      value: contract.offerSpecifics.downPayment,
      originalValue: contract.offerSpecifics.downPayment,
      category: 'price',
      isNegotiable: true,
      isRequired: false,
      validation: {
        min: 0,
        required: false,
      },
    });
  }

  return clauses;
}

/**
 * Extract timeline-related clauses
 */
function extractTimelineClauses(contract: Contract): ClauseData[] {
  const clauses: ClauseData[] = [];

  // Closing Date
  if (contract.agentOfferInfo?.closingDate) {
    clauses.push({
      id: 'closing-date',
      title: 'Closing Date',
      description: `The closing date shall be on or before ${contract.agentOfferInfo.closingDate}`,
      value: contract.agentOfferInfo.closingDate,
      originalValue: contract.agentOfferInfo.closingDate,
      category: 'timeline',
      isNegotiable: true,
      isRequired: true,
      validation: {
        required: true,
      },
    });
  }

  // Financing Commitment Date
  if (contract.offerSpecifics?.financingCommittmentDate) {
    clauses.push({
      id: 'financing-commitment-date',
      title: 'Financing Commitment Date',
      description: `Financing commitment due by ${contract.offerSpecifics.financingCommittmentDate}`,
      value: contract.offerSpecifics.financingCommittmentDate,
      originalValue: contract.offerSpecifics.financingCommittmentDate,
      category: 'timeline',
      isNegotiable: true,
      isRequired: false,
      validation: {
        required: false,
      },
    });
  }

  return clauses;
}

/**
 * Extract contingency-related clauses
 */
function extractContingencyClauses(contract: Contract): ClauseData[] {
  const clauses: ClauseData[] = [];

  // Appraisal Contingency
  if (
    contract.contingency?.appraisalContingency &&
    // Cast: truthiness above already narrows out NOT_SELECTED ('') — this recheck is redundant but harmless, kept as-is.
    (contract.contingency
      .appraisalContingency as CONTRACT_APPRAISAL_CONTINGENCY) !==
      CONTRACT_APPRAISAL_CONTINGENCY.NOT_SELECTED
  ) {
    const appraisalClause: ClauseData = {
      id: 'appraisal-contingency',
      title: 'Appraisal Contingency',
      description: getAppraisalContingencyDescription(
        contract.contingency.appraisalContingency
      ),
      value: contract.contingency.appraisalContingency,
      originalValue: contract.contingency.appraisalContingency,
      category: 'contingency',
      isNegotiable: true,
      isRequired: false,
    };

    // Add appraisal negotiation days as sub-clause
    if (contract.contingency.daysForAppraisalNegotiation) {
      appraisalClause.subClauses = [
        {
          id: 'appraisal-negotiation-days',
          title: 'Appraisal Negotiation Period',
          description: `Appraisal negotiation period: ${contract.contingency.daysForAppraisalNegotiation} days`,
          value: contract.contingency.daysForAppraisalNegotiation,
          originalValue: contract.contingency.daysForAppraisalNegotiation,
          category: 'contingency',
          isNegotiable: true,
          isRequired: false,
          parentClauseId: 'appraisal-contingency',
          validation: {
            min: 1,
            max: 30,
          },
        },
      ];
    }

    clauses.push(appraisalClause);
  }

  // Financing Contingency
  if (
    contract.contingency?.financingContingency &&
    // Cast: truthiness above already narrows out NOT_SELECTED ('') — this recheck is redundant but harmless, kept as-is.
    (contract.contingency
      .financingContingency as CONTRACT_FINANCING_CONTINGENCY) !==
      CONTRACT_FINANCING_CONTINGENCY.NOT_SELECTED
  ) {
    const financingClause: ClauseData = {
      id: 'financing-contingency',
      title: 'Financing Contingency',
      description: getFinancingContingencyDescription(contract),
      value: contract.contingency.financingContingency,
      originalValue: contract.contingency.financingContingency,
      category: 'contingency',
      isNegotiable: true,
      isRequired: false,
    };

    // Add financing type as sub-clause
    if (
      contract.contingency.financingContingencyType &&
      // Cast: truthiness above already narrows out NOT_SELECTED ('') — this recheck is redundant but harmless, kept as-is.
      (contract.contingency
        .financingContingencyType as CONTRACT_FINANCING_CONTINGENCY_TYPES) !==
        CONTRACT_FINANCING_CONTINGENCY_TYPES.NOT_SELECTED
    ) {
      financingClause.subClauses = [
        {
          id: 'financing-type',
          title: 'Financing Type',
          description: getFinancingTypeDescription(
            contract.contingency.financingContingencyType
          ),
          value: contract.contingency.financingContingencyType,
          originalValue: contract.contingency.financingContingencyType,
          category: 'contingency',
          isNegotiable: true,
          isRequired: false,
          parentClauseId: 'financing-contingency',
        },
      ];
    }

    clauses.push(financingClause);
  }

  return clauses;
}

/**
 * Extract inspection-related clauses
 */
function extractInspectionClauses(contract: Contract): ClauseData[] {
  const clauses: ClauseData[] = [];

  // Home Inspection Contingency
  if (
    contract.contingency?.inspectionContingency &&
    // Cast: truthiness above already narrows out NOT_SELECTED ('') — this recheck is redundant but harmless, kept as-is.
    (contract.contingency
      .inspectionContingency as CONTRACT_INSPECTION_CONTINGENCY) !==
      CONTRACT_INSPECTION_CONTINGENCY.NOT_SELECTED
  ) {
    const inspectionClause: ClauseData = {
      id: 'home-inspection-contingency',
      title: 'Home Inspection Contingency',
      description: getInspectionContingencyDescription(
        contract.contingency.inspectionContingency
      ),
      value: contract.contingency.inspectionContingency,
      originalValue: contract.contingency.inspectionContingency,
      category: 'inspection',
      isNegotiable: true,
      isRequired: false,
    };

    // Add inspection negotiation days as sub-clause
    if (contract.contingency.homeinspectionNegotiationDays) {
      inspectionClause.subClauses = [
        {
          id: 'inspection-negotiation-days',
          title: 'Inspection Negotiation Period',
          description: `Inspection negotiation period: ${contract.contingency.homeinspectionNegotiationDays} days`,
          value: contract.contingency.homeinspectionNegotiationDays,
          originalValue: contract.contingency.homeinspectionNegotiationDays,
          category: 'inspection',
          isNegotiable: true,
          isRequired: false,
          parentClauseId: 'home-inspection-contingency',
          validation: {
            min: 1,
            max: 30,
          },
        },
      ];
    }

    clauses.push(inspectionClause);
  }

  // Inspection Type
  if (contract.contingency?.inspectionType) {
    clauses.push({
      id: 'inspection-type',
      title: 'Inspection Type',
      description: `Inspection type: ${contract.contingency.inspectionType}`,
      value: contract.contingency.inspectionType,
      originalValue: contract.contingency.inspectionType,
      category: 'inspection',
      isNegotiable: true,
      isRequired: false,
    });
  }

  return clauses;
}

/**
 * Extract occupancy-related clauses
 */
function extractOccupancyClauses(contract: Contract): ClauseData[] {
  const clauses: ClauseData[] = [];

  // Post Close Occupancy
  if (
    contract.contingency?.postCloseOccupancy &&
    // Cast: truthiness above already narrows out NOT_SELECTED ('') — this recheck is redundant but harmless, kept as-is.
    (contract.contingency
      .postCloseOccupancy as CONTRACT_POST_CLOSE_OCCUPANCY) !==
      CONTRACT_POST_CLOSE_OCCUPANCY.NOT_SELECTED
  ) {
    const occupancyClause: ClauseData = {
      id: 'post-close-occupancy',
      title: 'Post Close Occupancy',
      description: getOccupancyDescription(contract.contingency),
      value: contract.contingency.postCloseOccupancy,
      originalValue: contract.contingency.postCloseOccupancy,
      category: 'occupancy',
      isNegotiable: true,
      isRequired: false,
    };

    // Add occupancy details as sub-clauses
    const subClauses: ClauseData[] = [];

    if (contract.contingency.postCloseOccupancyDays) {
      subClauses.push({
        id: 'occupancy-days',
        title: 'Occupancy Days',
        description: `Occupancy period: ${contract.contingency.postCloseOccupancyDays} days`,
        value: contract.contingency.postCloseOccupancyDays,
        originalValue: contract.contingency.postCloseOccupancyDays,
        category: 'occupancy',
        isNegotiable: true,
        isRequired: false,
        parentClauseId: 'post-close-occupancy',
        validation: {
          min: 1,
          max: 365,
        },
      });
    }

    if (contract.contingency.postCloseOccupancyRent) {
      subClauses.push({
        id: 'occupancy-rent',
        title: 'Occupancy Rent',
        description: `Daily rent: $${contract.contingency.postCloseOccupancyRent}`,
        value: contract.contingency.postCloseOccupancyRent,
        originalValue: contract.contingency.postCloseOccupancyRent,
        category: 'occupancy',
        isNegotiable: true,
        isRequired: false,
        parentClauseId: 'post-close-occupancy',
        validation: {
          min: 0,
        },
      });
    }

    if (subClauses.length > 0) {
      occupancyClause.subClauses = subClauses;
    }

    clauses.push(occupancyClause);
  }

  return clauses;
}

/**
 * Helper functions for generating descriptions
 */
function getAppraisalContingencyDescription(contingency: string): string {
  switch (contingency) {
    case CONTRACT_APPRAISAL_CONTINGENCY.AT_LEAST_PURCHASE_PRICE:
      return 'This contract is contingent upon the property appraising for at least the purchase price';
    case CONTRACT_APPRAISAL_CONTINGENCY.AT_LEAST_LISTING_PRICE:
      return 'This contract is contingent upon the property appraising for at least the listing price';
    case CONTRACT_APPRAISAL_CONTINGENCY.AT_LEST_WITHIN_RANGE:
      return 'This contract is contingent upon the property appraising within the specified range';
    case CONTRACT_APPRAISAL_CONTINGENCY.NONE:
      return 'No appraisal contingency';
    default:
      return 'Appraisal contingency terms';
  }
}

function getFinancingContingencyDescription(contract: Contract): string {
  const { financingContingency, financingContingencyType } =
    contract.contingency || {};

  let description =
    'This contract is contingent upon buyer obtaining financing';

  if (
    financingContingency ===
    CONTRACT_FINANCING_CONTINGENCY.AT_LEAST_PURCHASE_PRICE
  ) {
    description += ' for at least the purchase price';
  } else if (financingContingency === CONTRACT_FINANCING_CONTINGENCY.TIMELINE) {
    description += ' within the specified timeline';
  }

  if (financingContingencyType) {
    const typeDescription = getFinancingTypeDescription(
      financingContingencyType
    );
    description += ` (${typeDescription})`;
  }

  return description;
}

function getFinancingTypeDescription(type: string): string {
  switch (type) {
    case CONTRACT_FINANCING_CONTINGENCY_TYPES.MORTGAGE_FINANCING:
      return 'Mortgage Financing';
    case CONTRACT_FINANCING_CONTINGENCY_TYPES.SELLER_FINANCING:
      return 'Seller Financing';
    case CONTRACT_FINANCING_CONTINGENCY_TYPES.LOAN_ASSUMPTION:
      return 'Loan Assumption';
    case CONTRACT_FINANCING_CONTINGENCY_TYPES.COMMERCIAL:
      return 'Commercial Financing';
    default:
      return 'Financing';
  }
}

function getInspectionContingencyDescription(contingency: string): string {
  switch (contingency) {
    case CONTRACT_INSPECTION_CONTINGENCY.RESERVE_THE_RIGHT:
      return 'Buyer reserves the right to conduct home inspection';
    case CONTRACT_INSPECTION_CONTINGENCY.WAIVE_THR_RIGHT:
      return 'Buyer waives the right to home inspection';
    default:
      return 'Home inspection contingency terms';
  }
}

function getOccupancyDescription(contingency: any): string {
  const { postCloseOccupancy, postCloseOccupancyDays, postCloseOccupancyRent } =
    contingency;

  let description = 'Post-closing occupancy terms';

  if (postCloseOccupancyDays) {
    description += ` for ${postCloseOccupancyDays} days`;
  }

  if (postCloseOccupancyRent) {
    description += ` at $${postCloseOccupancyRent} per day`;
  }

  return description;
}

/**
 * Validate clause data
 */
export function validateClause(clause: ClauseData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (
    clause.isRequired &&
    (clause.value === null || clause.value === undefined || clause.value === '')
  ) {
    errors.push(`${clause.title} is required`);
  }

  if (clause.validation) {
    const { min, max, pattern } = clause.validation;

    if (
      min !== undefined &&
      typeof clause.value === 'number' &&
      clause.value < min
    ) {
      errors.push(`${clause.title} must be at least ${min}`);
    }

    if (
      max !== undefined &&
      typeof clause.value === 'number' &&
      clause.value > max
    ) {
      errors.push(`${clause.title} must be at most ${max}`);
    }

    if (
      pattern &&
      typeof clause.value === 'string' &&
      !new RegExp(pattern).test(clause.value)
    ) {
      errors.push(`${clause.title} format is invalid`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get clause by ID from categories
 */
export function getClauseById(
  categories: ClauseCategory[],
  clauseId: string
): ClauseData | null {
  for (const category of categories) {
    for (const clause of category.clauses) {
      if (clause.id === clauseId) {
        return clause;
      }
      if (clause.subClauses) {
        const subClause = clause.subClauses.find((sub) => sub.id === clauseId);
        if (subClause) {
          return subClause;
        }
      }
    }
  }
  return null;
}

/**
 * Update clause value
 */
export function updateClauseValue(
  categories: ClauseCategory[],
  clauseId: string,
  newValue: any
): ClauseCategory[] {
  return categories.map((category) => ({
    ...category,
    clauses: category.clauses.map((clause) => {
      if (clause.id === clauseId) {
        return { ...clause, value: newValue };
      }
      if (clause.subClauses) {
        return {
          ...clause,
          subClauses: clause.subClauses.map((subClause) =>
            subClause.id === clauseId
              ? { ...subClause, value: newValue }
              : subClause
          ),
        };
      }
      return clause;
    }),
  }));
}

/**
 * Enhance clauses with hierarchical IDs, properties, and negotiation status
 * @param contract - The contract data
 * @param contractId - The contract ID for generating hierarchical IDs
 * @returns Enhanced clause categories with hierarchical IDs and properties
 */
export function extractContractClausesWithIds(
  contract: Contract,
  contractId: string
): ClauseCategory[] {
  // Safety check for empty contractId
  if (!contractId) {
    console.warn(
      'extractContractClausesWithIds - Empty contractId provided, returning empty categories'
    );
    return [];
  }

  // Safety check for undefined contract
  if (!contract) {
    console.warn(
      'extractContractClausesWithIds - No contract data provided, returning empty categories'
    );
    return [];
  }

  const baseCategories = extractContractClauses(contract);

  // Add hierarchical IDs, properties, and negotiation status to each clause
  return baseCategories.map((category) => ({
    ...category,
    clauses: category.clauses.map((clause) => {
      try {
        const properties = generateClauseIdWithProperties(
          contractId,
          clause.id,
          {
            status: 'original',
            proposedBy: 'buyer',
            lastModifiedBy: 'buyer',
          }
        );

        return {
          ...clause,
          hierarchicalId: properties.hierarchicalId,
          negotiationStatus: 'original' as const,
          proposedBy: 'buyer' as const,
          properties,
        };
      } catch (error) {
        console.error(
          'Error generating clause properties for clause:',
          clause.id,
          'error:',
          error
        );
        // Return clause without properties if generation fails
        return {
          ...clause,
          hierarchicalId: `CL-${contractId}-${clause.id}`,
          negotiationStatus: 'original' as const,
          proposedBy: 'buyer' as const,
          properties: undefined,
        };
      }
    }),
  }));
}
